import { computed } from "vue";
import Api from "@/model/api";
import { useUploadStore } from "@/store/uploadStore";
import { storeToRefs } from "pinia";
import { useLogin } from "@/composables/login/useLogin";
import { useUserStore } from "@/store/userStore";
import { useUploadDialog } from "./useUploadDialog";
/**
 * useUpload 组合式函数
 * 用于管理文件上传状态和操作
 * @returns {Object} 包含上传状态和操作的方法
 */
export const useUpload = () => {
  const uploadStore = useUploadStore();
  const userStore = useUserStore();

  const {
    isUploading,
    currentUploadIndex,
    currentUploadAmount,
    currentProgress,
    uploadedFileList,
    inputFileElement,
  } = storeToRefs(uploadStore);

  const {
    action: { openUploadDialog },
    state: { DialogSceneEnum },
  } = useUploadDialog();

  const {
    state: { isLogin, LoginSceneEnum },
    action: { openLoginDialog },
  } = useLogin();

  // 计算属性：上传按钮文本
  const uploadButtonText = computed(() =>
    isUploading.value
      ? `（${currentUploadIndex.value}/${
          currentUploadAmount.value
        }）正在上传...${currentProgress.value.toFixed(2)}%`
      : "添加文件，转成云链"
  );

  /**
   * 处理上传错误
   */
  const handleUploadError = (error) => {
    if (error.response && error.response.status === 401) {
      userStore.logout();
      ElMessage({
        message: "登录已失效，请重新登录",
        type: "warning",
      });
      openLoginDialog();
    } else {
      ElMessage({
        message: "上传失败，请重试",
        type: "error",
      });
    }
  };

  /**
   * 上传单个文件
   * @param {string} name - 文件名称
   * @param {File} file - 文件对象
   * @returns {Promise<Object>} 上传结果
   */
  const uploadFile = async (name, file) => {
    try {
      currentProgress.value = 0;
      const uploadedFile = await Api.uploadFile(name, file, currentProgress);
      return uploadedFile;
    } catch (error) {
      handleUploadError(error);
      throw error;
    }
  };

  /**
   * 上传文件列表
   * @param {FileList} fileList - 文件列表
   */
  const uploadFileList = async (fileList) => {
    // 如果已登录，验证 token 是否有效
    if (isLogin.value) {
      const isTokenValid = await Api.verifyToken();
      if (!isTokenValid) {
        userStore.logout();
        ElMessage({
          message: "登录已失效，请重新登录",
          type: "warning",
        });
        openLoginDialog();
        isUploading.value = false;
        inputFileElement.value.value = null;
        return;
      }
    }

    isUploading.value = true;
    currentUploadAmount.value = fileList.length;
    uploadedFileList.value = [];
    let hasError = false;

    for (let i = 0; i < fileList.length; i++) {
      currentUploadIndex.value = i + 1;
      const file = fileList[i];
      try {
        const result = await uploadFile(file.name, file);

        console.log("上传成功", result);
        uploadedFileList.value.push({
          url: result.url,
          name: result.name,
          object_id: result.object_id,
        });
      } catch (error) {
        hasError = true;
        break;
      }
    }

    isUploading.value = false;
    inputFileElement.value.value = null;

    // 根据登录状态显示不同弹窗
    if (!hasError) {
      if (isLogin.value) {
        // 已登录用户显示上传成功弹窗
        openUploadDialog(DialogSceneEnum.PERMANENT_FILE);
      } else {
        // 先显示上传成功提示
        ElMessage({
          message: "文件上传成功",
          type: "success",
        });
        // 未登录用户显示登录弹窗
        openLoginDialog(LoginSceneEnum.UPLOAD);
      }
    }
  };

  /**
   * 处理上传按钮点击
   */
  const handleUploadButtonClick = () => {
    inputFileElement.value.click();
  };

  /**
   * 处理文件选择变化
   */
  const handleFileChange = async (event) => {
    const files = event.target.files;
    if (files.length === 0) return;

    await uploadFileList(files);
  };

  return {
    state: {
      uploadButtonText,
      inputFileElement,
      isUploading,
      uploadedFileList,
    },
    action: {
      uploadFileList,
      handleUploadButtonClick,
      handleFileChange,
    },
  };
};
